<template>
  <div v-if="visible" class="qr-modal-overlay" @click.self="closeModal">
    <section
      class="qr-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Escaner QR"
    >
      <header class="qr-modal-header">
        <h3>Escanear zona por QR</h3>
        <button class="close-btn" type="button" @click="closeModal">
          Cerrar
        </button>
      </header>

      <p class="qr-modal-subtitle">
        Alinea el codigo dentro del recuadro para reconocer la zona
        automaticamente.
      </p>

      <div class="camera-controls">
        <label for="qr-camera-select" class="camera-label">Camara</label>
        <select
          id="qr-camera-select"
          v-model="selectedCameraId"
          class="camera-select"
          :disabled="!cameraOptions.length"
          @change="handleCameraSelection"
        >
          <option
            v-for="camera in cameraOptions"
            :key="camera.deviceId"
            :value="camera.deviceId"
          >
            {{ camera.label }}
          </option>
        </select>
        <button
          type="button"
          class="toggle-camera-btn"
          :disabled="cameraOptions.length < 2"
          @click="toggleCamera"
        >
          Alternar
        </button>
      </div>

      <div class="qr-video-wrapper">
        <video ref="video" class="qr-video" autoplay playsinline muted></video>
        <canvas ref="canvas" class="qr-canvas"></canvas>

        <div class="scan-guide" :class="{ busy: isDecoding }">
          <div class="scan-frame">
            <span class="corner top-left"></span>
            <span class="corner top-right"></span>
            <span class="corner bottom-left"></span>
            <span class="corner bottom-right"></span>
            <span
              v-if="!isDecoding && !errorMessage"
              class="scan-line"
              aria-hidden="true"
            ></span>
          </div>
          <p class="scan-hint">Coloca el QR centrado en este recuadro</p>
        </div>
      </div>

      <p v-if="statusMessage" class="qr-status">{{ statusMessage }}</p>
      <p v-if="errorMessage" class="qr-error">{{ errorMessage }}</p>

      <div class="qr-actions">
        <button type="button" class="secondary-btn" @click="closeModal">
          Cancelar
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import jsQR from "jsqr";

export default {
  name: "QRZoneScannerModal",

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      errorMessage: "",
      statusMessage: "Inicializando camara...",
      stream: null,
      rafId: null,
      isDecoding: false,
      cameraOptions: [],
      selectedCameraId: "",
      barcodeDetector: null,
      barcodeDetectInFlight: false,
      lastBarcodeDetectAt: 0,
      lastDetectedValue: "",
      lastDetectedAt: 0,
    };
  },

  watch: {
    visible: {
      immediate: true,
      async handler(newValue) {
        if (newValue) {
          await this.startScanner();
          return;
        }
        this.stopScanner();
      },
    },
  },

  beforeDestroy() {
    this.stopScanner();
  },

  methods: {
    processDetectedCode(rawValue) {
      const detected = String(rawValue || "").trim();
      const now = Date.now();

      if (!detected) return false;

      // Evita disparos repetidos del mismo QR en milisegundos consecutivos.
      if (
        this.lastDetectedValue === detected &&
        now - this.lastDetectedAt < 1500
      ) {
        return false;
      }

      this.lastDetectedValue = detected;
      this.lastDetectedAt = now;
      this.isDecoding = true;
      this.statusMessage = "QR detectado. Validando zona...";
      this.$emit("scan", detected);
      return true;
    },

    decodeQRCode(imageData, width, height) {
      return jsQR(imageData.data, width, height, {
        inversionAttempts: "attemptBoth",
      });
    },

    decodeQRCodeMirrored(imageData, width, height) {
      const source = imageData.data;
      const mirrored = new Uint8ClampedArray(source.length);

      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const srcIndex = (y * width + x) * 4;
          const dstX = width - 1 - x;
          const dstIndex = (y * width + dstX) * 4;

          mirrored[dstIndex] = source[srcIndex];
          mirrored[dstIndex + 1] = source[srcIndex + 1];
          mirrored[dstIndex + 2] = source[srcIndex + 2];
          mirrored[dstIndex + 3] = source[srcIndex + 3];
        }
      }

      return jsQR(mirrored, width, height, {
        inversionAttempts: "attemptBoth",
      });
    },

    decodeQRCodeFromFrame(context, width, height) {
      const fullFrameData = context.getImageData(0, 0, width, height);
      let result = this.decodeQRCode(fullFrameData, width, height);
      if (result && result.data) return result;

      // Segundo intento: recorte centrado donde el usuario alinea el QR.
      const side = Math.floor(Math.min(width, height) * 0.62);
      const sx = Math.floor((width - side) / 2);
      const sy = Math.floor((height - side) / 2);
      const centerData = context.getImageData(sx, sy, side, side);
      result = this.decodeQRCode(centerData, side, side);
      if (result && result.data) return result;

      // Tercer intento: frame completo espejado (algunas cámaras frontales).
      result = this.decodeQRCodeMirrored(fullFrameData, width, height);
      if (result && result.data) return result;

      // Cuarto intento: recorte central espejado.
      result = this.decodeQRCodeMirrored(centerData, side, side);
      return result;
    },

    closeModal() {
      this.$emit("close");
    },

    async tryDecodeWithBarcodeDetector(videoEl) {
      if (
        !this.barcodeDetector ||
        this.barcodeDetectInFlight ||
        this.isDecoding
      ) {
        return;
      }

      const now = Date.now();
      if (now - this.lastBarcodeDetectAt < 120) {
        return;
      }

      this.lastBarcodeDetectAt = now;
      this.barcodeDetectInFlight = true;

      try {
        const detections = await this.barcodeDetector.detect(videoEl);
        if (!detections || !detections.length) return;

        const first = detections.find((d) => d.rawValue) || detections[0];
        this.processDetectedCode(first?.rawValue || "");
      } catch (error) {
        // Silenciar: no todos los navegadores soportan BarcodeDetector estable.
      } finally {
        this.barcodeDetectInFlight = false;
      }
    },

    async startScanner() {
      this.errorMessage = "";
      this.statusMessage = "Inicializando camara...";
      this.isDecoding = false;

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.errorMessage = "El dispositivo no soporta acceso a camara.";
        this.statusMessage = "";
        return;
      }

      this.stopScanner();

      try {
        const preferredConstraints = this.selectedCameraId
          ? {
              deviceId: { exact: this.selectedCameraId },
              width: { ideal: 1280 },
              height: { ideal: 720 },
            }
          : {
              facingMode: { ideal: "environment" },
              width: { ideal: 1280 },
              height: { ideal: 720 },
            };

        try {
          this.stream = await navigator.mediaDevices.getUserMedia({
            video: preferredConstraints,
            audio: false,
          });
        } catch (preferredError) {
          this.stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: { ideal: "environment" },
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
            audio: false,
          });
        }

        const video = this.$refs.video;
        if (!video) return;

        video.srcObject = this.stream;
        this.statusMessage = "Apunta al codigo QR de la zona";

        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {
            // En algunas WebViews móviles el promise de play() puede fallar aunque el stream esté listo.
          });
        }

        await this.refreshCameraDevices();

        if (!this.barcodeDetector && "BarcodeDetector" in window) {
          try {
            this.barcodeDetector = new window.BarcodeDetector({
              formats: ["qr_code"],
            });
          } catch (error) {
            this.barcodeDetector = null;
          }
        }

        this.scanLoop();
      } catch (error) {
        this.errorMessage =
          "No se pudo abrir la camara. Revisa permisos del dispositivo.";
        this.statusMessage = "";
      }
    },

    stopScanner() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }

      const video = this.$refs.video;
      if (video) {
        try {
          video.pause();
        } catch (e) {
          // no-op
        }
        video.srcObject = null;
      }

      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }

      this.isDecoding = false;
      this.barcodeDetectInFlight = false;
    },

    async refreshCameraDevices() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        this.cameraOptions = [];
        return;
      }

      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cameras = devices
          .filter((device) => device.kind === "videoinput")
          .map((camera, index) => ({
            deviceId: camera.deviceId,
            label: camera.label || `Camara ${index + 1}`,
          }));

        this.cameraOptions = cameras;

        const activeTrack = this.stream?.getVideoTracks?.()[0];
        const activeDeviceId = activeTrack?.getSettings?.().deviceId || "";

        if (activeDeviceId) {
          this.selectedCameraId = activeDeviceId;
          return;
        }

        const exists = cameras.some(
          (camera) => camera.deviceId === this.selectedCameraId,
        );
        if (!exists && cameras.length > 0) {
          const backCamera = cameras.find((camera) =>
            /back|rear|trasera|environment/i.test(camera.label),
          );
          this.selectedCameraId = (backCamera || cameras[0]).deviceId;
        }
      } catch (error) {
        this.cameraOptions = [];
      }
    },

    async handleCameraSelection() {
      if (!this.selectedCameraId) return;
      await this.startScanner();
    },

    async toggleCamera() {
      if (this.cameraOptions.length < 2) return;

      const currentIndex = this.cameraOptions.findIndex(
        (camera) => camera.deviceId === this.selectedCameraId,
      );
      const safeIndex = currentIndex >= 0 ? currentIndex : 0;
      const nextIndex = (safeIndex + 1) % this.cameraOptions.length;
      this.selectedCameraId = this.cameraOptions[nextIndex].deviceId;
      await this.startScanner();
    },

    scanLoop() {
      if (!this.visible) return;

      const video = this.$refs.video;
      const canvas = this.$refs.canvas;
      if (!video || !canvas) {
        this.rafId = requestAnimationFrame(() => this.scanLoop());
        return;
      }

      if (video.readyState < 2 || this.isDecoding) {
        this.rafId = requestAnimationFrame(() => this.scanLoop());
        return;
      }

      const width = video.videoWidth;
      const height = video.videoHeight;
      if (!width || !height) {
        this.rafId = requestAnimationFrame(() => this.scanLoop());
        return;
      }

      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d", { willReadFrequently: true });
      context.drawImage(video, 0, 0, width, height);

      const qrResult = this.decodeQRCodeFromFrame(context, width, height);

      if (qrResult && qrResult.data) {
        this.processDetectedCode(qrResult.data);
      } else if (video) {
        this.tryDecodeWithBarcodeDetector(video);
      }

      this.rafId = requestAnimationFrame(() => this.scanLoop());
    },

    setBusy(isBusy) {
      this.isDecoding = isBusy;
      if (!isBusy && !this.errorMessage) {
        this.statusMessage = "Apunta al codigo QR de la zona";
      }
    },

    setError(message) {
      this.errorMessage = message || "";
      this.statusMessage = this.errorMessage
        ? ""
        : "Apunta al codigo QR de la zona";
      this.isDecoding = false;
    },
  },
};
</script>

<style scoped>
.qr-modal-overlay {
  position: fixed;
  inset: 0;
  background: radial-gradient(
      circle at top right,
      rgba(16, 118, 49, 0.26),
      transparent 55%
    ),
    rgba(10, 13, 11, 0.72);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  backdrop-filter: blur(2px);
}

.qr-modal {
  width: min(560px, 100%);
  background: linear-gradient(180deg, #ffffff 0%, #f5f8f6 100%);
  border-radius: 16px;
  border: 1px solid #dce8de;
  overflow: hidden;
  box-shadow: 0 18px 44px rgba(12, 26, 17, 0.36);
}

.qr-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #e6efe8;
  background: linear-gradient(135deg, #107631 0%, #15803d 55%, #3a9f59 100%);
}

.qr-modal-header h3 {
  margin: 0;
  font-size: 16px;
  color: #f5fff7;
  font-weight: 700;
}

.close-btn {
  border: 1px solid rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.14);
  color: #f6fff8;
  font-weight: 600;
  border-radius: 8px;
  height: 32px;
  min-width: 72px;
  padding: 0 10px;
  cursor: pointer;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.25);
}

.qr-modal-subtitle {
  margin: 12px 16px 10px;
  color: #365242;
  font-size: 13px;
  line-height: 1.35;
}

.camera-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 16px 10px;
}

.camera-label {
  font-size: 12px;
  color: #2f4f3d;
  font-weight: 600;
}

.camera-select {
  flex: 1;
  min-width: 0;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #c6d8c9;
  background: #ffffff;
  color: #2a4233;
  padding: 0 10px;
}

.toggle-camera-btn {
  height: 36px;
  border-radius: 8px;
  border: 1px solid #b4d6bc;
  background: #edf7f0;
  color: #205f36;
  padding: 0 10px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.toggle-camera-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.qr-video-wrapper {
  position: relative;
  background: #0f1411;
  aspect-ratio: 16 / 9;
  margin: 0 16px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #1f3f2a;
}

.qr-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.qr-canvas {
  display: none;
}

.scan-guide {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(4, 12, 8, 0.35) 0%,
    rgba(4, 12, 8, 0.15) 40%,
    rgba(4, 12, 8, 0.35) 100%
  );
}

.scan-frame {
  position: relative;
  width: min(64vw, 220px);
  height: min(64vw, 220px);
  border: 2px solid rgba(190, 255, 212, 0.6);
  border-radius: 18px;
  box-shadow: inset 0 0 0 999px rgba(9, 12, 9, 0.2),
    0 0 24px rgba(111, 255, 166, 0.22);
}

.corner {
  position: absolute;
  width: 24px;
  height: 24px;
  border-color: #a9ffc8;
  border-style: solid;
  border-width: 0;
}

.top-left {
  top: -3px;
  left: -3px;
  border-top-width: 4px;
  border-left-width: 4px;
  border-top-left-radius: 12px;
}

.top-right {
  top: -3px;
  right: -3px;
  border-top-width: 4px;
  border-right-width: 4px;
  border-top-right-radius: 12px;
}

.bottom-left {
  bottom: -3px;
  left: -3px;
  border-bottom-width: 4px;
  border-left-width: 4px;
  border-bottom-left-radius: 12px;
}

.bottom-right {
  bottom: -3px;
  right: -3px;
  border-bottom-width: 4px;
  border-right-width: 4px;
  border-bottom-right-radius: 12px;
}

.scan-line {
  position: absolute;
  left: 14px;
  right: 14px;
  top: 18px;
  height: 3px;
  border-radius: 3px;
  background: linear-gradient(90deg, #57ff98 0%, #ccffe1 50%, #57ff98 100%);
  box-shadow: 0 0 14px rgba(122, 255, 170, 0.9);
  animation: scan-move 2s ease-in-out infinite;
}

.scan-guide.busy .scan-frame {
  border-color: rgba(157, 255, 193, 0.9);
}

.scan-hint {
  margin: 12px 0 0;
  font-size: 12px;
  color: #e8fff0;
  background: rgba(7, 23, 13, 0.56);
  border: 1px solid rgba(123, 255, 171, 0.4);
  border-radius: 999px;
  padding: 6px 10px;
}

.qr-status,
.qr-error {
  margin: 12px 16px 0;
  font-size: 14px;
  border-radius: 10px;
  padding: 10px 12px;
}

.qr-status {
  color: #13542e;
  background: #e8f7ec;
  border: 1px solid #c3e7ce;
}

.qr-error {
  color: #b91c1c;
  background: #fdecec;
  border: 1px solid #f6c0c0;
}

.qr-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 16px 16px;
}

.secondary-btn {
  padding: 10px 14px;
  border: 1px solid #c8d8cc;
  background: #ffffff;
  color: #2e4a37;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
}

.secondary-btn:hover {
  background: #eff6f1;
}

@keyframes scan-move {
  0% {
    top: 18px;
    opacity: 0.85;
  }
  50% {
    top: calc(100% - 22px);
    opacity: 1;
  }
  100% {
    top: 18px;
    opacity: 0.85;
  }
}

@media (max-width: 480px) {
  .scan-frame {
    width: min(70vw, 190px);
    height: min(70vw, 190px);
  }
}
</style>
